import * as React from 'react';
import {
  ColorArea as AriaColorArea,
  ColorField as AriaColorField,
  ColorPicker as AriaColorPicker,
  ColorSlider as AriaColorSlider,
  ColorThumb as AriaColorThumb,
  SliderTrack as AriaSliderTrack,
  ColorPickerStateContext,
  parseColor,
  composeRenderProps,
} from 'react-aria-components';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { XIcon } from 'lucide-react';
import { Input } from '../ui/input';

const ColorField = AriaColorField;
const ColorPicker = AriaColorPicker;

function ColorSlider({ className, ...props }: any) {
  return (
    <AriaColorSlider
      className={composeRenderProps(className, (className) =>
        cn('py-1', className),
      )}
      {...props}
    />
  );
}

function ColorArea({ className, ...props }: any) {
  return (
    <AriaColorArea
      className={composeRenderProps(className, (className) =>
        cn('h-[150px] w-full rounded-lg', className),
      )}
      {...props}
    />
  );
}

function SliderTrack({ className, style, ...props }: any) {
  return (
    <AriaSliderTrack
      className={composeRenderProps(className, (className) =>
        cn('h-2 w-full rounded-full', className),
      )}
      style={({ defaultStyle }) => ({
        ...style,
        background: `${defaultStyle.background},
          repeating-conic-gradient(
            #fff 0 90deg,
            rgba(0,0,0,.3) 0 180deg) 
          0% -25%/6px 6px`,
      })}
      {...props}
    />
  );
}

function ColorThumb({ className, ...props }: any) {
  return (
    <AriaColorThumb
      className={composeRenderProps(className, (className) =>
        cn('z-50 size-3 rounded-full ring-2 ring-stroke-white-0', className),
      )}
      {...props}
    />
  );
}

const EyeDropperButton = React.forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement>
>(({ ...rest }, forwardedRef) => {
  const state = React.useContext(ColorPickerStateContext)!;

  // eslint-disable-next-line
  // @ts-ignore
  if (typeof EyeDropper === 'undefined') {
    return null;
  }

  return (
    <button
      ref={forwardedRef}
      aria-label='Eye dropper'
      onClick={() => {
        // eslint-disable-next-line
        // @ts-ignore
        new EyeDropper()
          .open()
          .then((result: { sRGBHex: string }) =>
            state.setColor(parseColor(result.sRGBHex)),
          );
      }}
      {...rest}
    />
  );
});
EyeDropperButton.displayName = 'EyeDropperButton';

interface ColorPickerProps {
  value?: string;
  onChange?: (color: string) => void;
  onClose?: () => void;
}

const ColorPickerComponent = ({ value, onChange, onClose }: ColorPickerProps) => {
  const [color, setColor] = React.useState(parseColor(value || '#FFFFFF'));
  const [hexInput, setHexInput] = React.useState(value?.replace('#', '') || 'FFFFFF');

  React.useEffect(() => {
    if (value) {
      setColor(parseColor(value));
      setHexInput(value.replace('#', ''));
    }
  }, [value]);

  const handleColorChange = (newColor: any) => {
    setColor(newColor);
    const hexValue = newColor.toString('hex').replace('#', '');
    setHexInput(hexValue);
    onChange?.(hexValue);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase();
    setHexInput(newValue);
    
    // Only update color if it's a valid hex
    if (/^[0-9A-F]{6}$/i.test(newValue)) {
      const newColor = parseColor(`#${newValue}`);
      setColor(newColor);
      onChange?.(newValue);
    }
  };

  return (
    <div className='flex w-[272px] flex-col gap-3 rounded-2xl bg-white p-4 shadow-lg ring-1 ring-inset ring-gray-200'>
      <div className="flex justify-between items-center mb-2">
        <div className="text-sm font-medium">Choisir une couleur</div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        )}
      </div>

      <ColorPicker value={color} onChange={handleColorChange}>
        <ColorArea
          colorSpace='hsl'
          xChannel='saturation'
          yChannel='lightness'
        >
          <ColorThumb className='ring-white' />
        </ColorArea>

        <ColorSlider colorSpace='hsl' channel='hue'>
          <SliderTrack>
            <ColorThumb className='top-1/2' />
          </SliderTrack>
        </ColorSlider>

        <div className='flex flex-col gap-2 mt-2'>
          <div className='text-xs text-gray-600'>
            Couleur sélectionnée
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="w-8 h-8 rounded border border-gray-200"
              style={{ backgroundColor: color.toString('hex') }}
            />
            <div className="flex-1">
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">#</span>
                <Input
                  value={hexInput}
                  onChange={handleHexInputChange}
                  className="h-8 font-mono uppercase"
                  maxLength={6}
                  pattern="[0-9A-Fa-f]*"
                />
              </div>
            </div>
          </div>
        </div>
      </ColorPicker>
    </div>
  );
}

export {
  ColorPicker as Root,
  ColorField as Field,
  ColorArea as Area,
  ColorSlider as Slider,
  SliderTrack,
  ColorThumb as Thumb,
  EyeDropperButton,
}; 

export { ColorPickerComponent };
